import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api, { apiErr } from "@/lib/api";
import { Trophy, MapPin, CalendarDays, Sparkles, ChevronRight } from "lucide-react";
import useSeo from "@/components/useSeo";

const BracketView = ({ tid, matches, color, logoById = {} }) => {
  const rounds = {};
  const ord = { W: "1", L: "2", GF: "3" };
  const vis = matches.filter((m) => !(m.bracket === "GF" && m.round === 1 && !m.team_a_id && !m.winner_id));
  vis.forEach((m) => { const key = `${ord[m.bracket] || "1"}|${String(m.round).padStart(2, "0")}`; (rounds[key] = rounds[key] || []).push(m); });
  const keys = Object.keys(rounds).sort();
  if (keys.length === 0) return null;
  return (
    <div className="overflow-x-auto pb-2" data-testid="public-bracket">
      <div className="flex gap-6 min-w-max">
        {keys.map((r) => (
          <div key={r} className="flex flex-col justify-around gap-4 min-w-[190px]">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">{rounds[r][0].round_name}</p>
            {rounds[r].sort((a, b) => a.position - b.position).map((m) => (
              <Link key={m.id} to={`/tournament/${tid}/match/${m.id}`} className="block rounded-lg border border-border bg-card overflow-hidden hover:border-primary/60 transition-colors" data-testid={`public-match-${m.id}`}>
                {m.status === "live" && <div className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 flex items-center gap-1">● LIVE — tap to watch</div>}
                {["a", "b"].map((s) => {
                  const id = s === "a" ? m.team_a_id : m.team_b_id;
                  const nm = s === "a" ? m.team_a_name : m.team_b_name;
                  const sc = s === "a" ? m.score_a : m.score_b;
                  const win = m.winner_id && m.winner_id === id;
                  return (
                    <div key={s} className={`flex items-center justify-between px-3 py-2 text-sm ${s === "a" ? "border-b border-border" : ""} ${win ? "font-semibold" : ""}`} style={win ? { color } : {}}>
                      <span className="flex items-center gap-2 truncate min-w-0">
                        {logoById[id] ? <img src={logoById[id]} alt="" className="h-5 w-5 rounded object-cover shrink-0" /> : null}
                        <span className="truncate">{nm || <span className="text-muted-foreground italic">TBD</span>}</span>
                      </span>
                      <span className="tabular-nums text-muted-foreground">{sc ?? ""}</span>
                    </div>
                  );
                })}
              </Link>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function PublicTournament() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/interclub/public/tournament/${id}`).then((r) => setData(r.data)).catch((e) => setErr(apiErr(e))); }, [id]);
  useSeo({
    title: data?.tournament?.name ? `${data.tournament.name} · Live Standings — SphereHyx` : "Tournament — SphereHyx",
    description: data?.tournament?.name
      ? `Live standings, fixtures, brackets and results for ${data.tournament.name}${data?.host?.name ? `, hosted by ${data.host.name}` : ""}. Powered by SphereHyx.`
      : "Live tournament standings, fixtures and brackets on SphereHyx.",
    canonical: `https://spherehyx.com/tournament/${id}`,
  });

  if (err) return <div className="min-h-screen flex items-center justify-center bg-background grain px-4"><div className="text-center"><h1 className="font-heading font-bold text-2xl">Tournament not found</h1><Link to="/"><Button className="mt-6" variant="outline">Go home</Button></Link></div></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const { tournament: t, teams, host, matches } = data;
  const color = host?.primary_color || "#007AFF";
  const _lum = (hex) => { const h = (hex || "").replace("#", ""); if (h.length < 6) return 1; const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16); return (0.299 * r + 0.587 * g + 0.114 * b) / 255; };
  const accent = _lum(color) < 0.4 ? "#60a5fa" : color;
  const logoById = {};
  (teams || []).forEach((tm) => { logoById[tm.id] = tm.logo; });
  const bracketMatches = (matches || []).filter((m) => m.stage === "knockout");
  const leagueMatches = (matches || []).filter((m) => m.stage !== "knockout");

  const StatusTag = ({ m }) => {
    if (m.status === "live") return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-500"><span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" /> LIVE</span>;
    if (m.status === "completed") return <span className="text-[11px] font-semibold text-emerald-500">Final</span>;
    return <span className="text-[11px] text-muted-foreground">{m.kickoff ? m.kickoff.replace("T", " ") : "Scheduled"}</span>;
  };

  return (
    <div className="min-h-screen bg-background grain">
      <div className="h-44 w-full flex items-center px-6" style={{ background: `linear-gradient(135deg, ${color}, ${host?.secondary_color || color})` }}>
        <div className="max-w-3xl mx-auto w-full flex items-center gap-4">
          {host?.logo ? <img src={host.logo} alt="" className="h-16 w-16 rounded-xl object-cover border-2 border-white/30" /> : <div className="h-16 w-16 rounded-xl bg-white/20" />}
          <div>
            <p className="text-white/80 text-xs uppercase tracking-wide">{host?.name}</p>
            <h1 className="text-white font-heading font-bold text-3xl" data-testid="public-tournament-name">{t?.name}</h1>
            <div className="flex flex-wrap gap-4 mt-1 text-white/90 text-sm">
              {t?.start_date && <span className="inline-flex items-center gap-1.5"><CalendarDays size={14} />{t.start_date}{t.end_date ? ` – ${t.end_date}` : ""}</span>}
              {t?.location && <span className="inline-flex items-center gap-1.5"><MapPin size={14} />{t.location}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        {t?.description && <p className="text-muted-foreground whitespace-pre-wrap">{t.description}</p>}

        {t?.closed && t?.champion_name && (
          <div className="rounded-2xl p-6 text-center text-white" style={{ background: `linear-gradient(135deg, ${color}, ${host?.secondary_color || color})` }} data-testid="public-champion">
            <Trophy size={30} className="mx-auto mb-1" />
            <p className="text-xs uppercase tracking-wide opacity-80">Champion</p>
            <p className="font-heading font-bold text-2xl">{t.champion_name}</p>
          </div>
        )}

        <div>
          <h2 className="font-heading font-bold text-xl mb-3">Standings</h2>
          <div className="rounded-2xl border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm" data-testid="public-standings">
              <thead><tr className="text-xs text-muted-foreground text-left border-b border-border">
                <th className="py-3 px-4">#</th><th>Team</th><th className="px-2 text-center">P</th><th className="px-2 text-center">W</th><th className="px-2 text-center">D</th><th className="px-2 text-center">L</th><th className="px-4 text-center">Pts</th>
              </tr></thead>
              <tbody>
                {teams.map((tm, i) => (
                  <tr key={tm.id} className="border-b border-border/60 last:border-0">
                    <td className="py-3 px-4 text-muted-foreground">{i + 1}</td>
                    <td className="font-medium">
                      <span className="flex items-center gap-2">
                        {tm.logo ? <img src={tm.logo} alt="" className="h-6 w-6 rounded object-cover border border-border shrink-0" /> : null}
                        <span>{tm.name}</span>
                      </span>
                    </td>
                    <td className="px-2 text-center">{tm.played || 0}</td>
                    <td className="px-2 text-center">{tm.won || 0}</td>
                    <td className="px-2 text-center">{tm.drawn || 0}</td>
                    <td className="px-2 text-center">{tm.lost || 0}</td>
                    <td className="px-4 text-center font-bold" style={{ color: accent }}>{tm.points || 0}</td>
                  </tr>
                ))}
                {teams.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">No teams yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {leagueMatches.length > 0 && (
          <div>
            <h2 className="font-heading font-bold text-xl mb-3">Matches</h2>
            <div className="space-y-2" data-testid="public-matches">
              {leagueMatches.map((m) => (
                <Link key={m.id} to={`/tournament/${id}/match/${m.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 hover:border-primary/60 transition-colors" data-testid={`public-match-${m.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{m.team_a_name} <span className="text-muted-foreground">vs</span> {m.team_b_name}</p>
                    <div className="mt-0.5"><StatusTag m={m} /></div>
                  </div>
                  <span className="font-heading font-bold tabular-nums">{m.score_a ?? 0} - {m.score_b ?? 0}</span>
                  <ChevronRight size={16} className="text-muted-foreground shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {bracketMatches.length > 0 && (
          <div>
            <h2 className="font-heading font-bold text-xl mb-3">Bracket <span className="text-xs font-normal text-muted-foreground">· tap a match to watch</span></h2>
            <BracketView tid={id} matches={bracketMatches} color={accent} logoById={logoById} />
          </div>
        )}

        <div className="rounded-xl border border-dashed border-border p-5 text-center">
          <Sparkles size={18} className="mx-auto text-primary mb-1" />
          <p className="text-sm font-medium">Powered by SphereHyx</p>
          <p className="text-xs text-muted-foreground mt-0.5">Manage your club, teams and tournaments in one place.</p>
          <Link to="/register"><Button variant="outline" size="sm" className="mt-3" data-testid="public-tournament-cta">Create your free club</Button></Link>
        </div>
      </div>
    </div>
  );
}
